import { useRef, useState } from "react";

import { spanned } from "@/flow/block";
import type { BBoxNorm } from "@/viewer/bbox";

interface Props {
  /** Called with the dragged box, page-normalised, when the pointer is released. */
  onBox: (box: BBoxNorm) => void;
  label: string;
}

type Point = { x: number; y: number };

/** A drag shorter than this, in CSS pixels, is a stray click rather than a box. */
const MIN_DRAG = 6;

/**
 * A layer over the take-off sheet that turns a drag into a box instead of a pan, so an
 * estimator can draw around a block of panels (Phase 7 K4). It covers the sheet exactly,
 * so its own size is the sheet's and a pointer position normalises against it.
 */
export function BoxSelect({ onBox, label }: Props) {
  const [from, setFrom] = useState<Point | null>(null);
  const [to, setTo] = useState<Point | null>(null);
  const start = useRef<{ x: number; y: number } | null>(null);

  const norm = (event: React.PointerEvent<HTMLDivElement>): Point => {
    const rect = event.currentTarget.getBoundingClientRect();
    const clamp = (v: number) => Math.min(1, Math.max(0, v));
    return {
      x: clamp((event.clientX - rect.left) / Math.max(1, rect.width)),
      y: clamp((event.clientY - rect.top) / Math.max(1, rect.height)),
    };
  };

  const box = from && to ? spanned(from, to) : null;

  return (
    <div
      className="absolute inset-0 z-10 cursor-crosshair touch-none"
      role="application"
      aria-label={label}
      onPointerDown={(event) => {
        if (event.button !== 0) return;
        event.stopPropagation();
        event.currentTarget.setPointerCapture?.(event.pointerId);
        start.current = { x: event.clientX, y: event.clientY };
        const p = norm(event);
        setFrom(p);
        setTo(p);
      }}
      onPointerMove={(event) => {
        if (!start.current) return;
        event.stopPropagation();
        setTo(norm(event));
      }}
      onPointerUp={(event) => {
        if (!start.current || !from) return;
        event.stopPropagation();
        const moved = Math.hypot(event.clientX - start.current.x, event.clientY - start.current.y);
        const end = norm(event);
        start.current = null;
        setFrom(null);
        setTo(null);
        if (moved >= MIN_DRAG) onBox(spanned(from, end));
      }}
    >
      {box ? (
        <div
          data-testid="box-select-band"
          className="pointer-events-none absolute border-2 border-dashed border-accent bg-accent-soft/30"
          style={{
            left: `${box.x0 * 100}%`,
            top: `${box.y0 * 100}%`,
            width: `${(box.x1 - box.x0) * 100}%`,
            height: `${(box.y1 - box.y0) * 100}%`,
          }}
        />
      ) : null}
    </div>
  );
}
